import {
  MenuItem,
  Stack,
  useTheme,
  Popover,
  Typography,
  Divider,
  MenuList,
} from "@mui/material";
import useStyles from "./styles";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

import { useEffect, useState } from "react";
import { Option } from "..";

interface ModalDateTimeProps {
  anchorEl: HTMLElement | null;
  setAnchorEl: (value: HTMLDivElement | null) => void;
  optionsTimesRange: Option[];
  value: dateFilter;
  onChange: (value: dateFilter) => void;
  onClose: () => void;
  disabled?: boolean;
}

dayjs.extend(duration);
const ModalDateTime: React.FC<ModalDateTimeProps> = ({
  anchorEl,
  setAnchorEl,
  optionsTimesRange,
  value,
  onChange,
  onClose,
  disabled,
}) => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const handleClose = () => {
    setAnchorEl(null);
    onClose();
  };
  const [intervalSeleted, setIntervalSelected] = useState<Option | null>();
  const { timeStart, timeEnd } = value ?? {
    timeStart: new Date(),
    timeEnd: new Date().getMonth() - 1,
  };

  useEffect(() => {
    const autoIntervalSelect = optionsTimesRange.find((option) => {
      const calcIntervl = calculateInterval(option);
      if (
        dayjs(timeStart).startOf("minute").format("YYYY-MM-DD HH:mm:ss") ===
          dayjs(calcIntervl.timeStart)
            .startOf("minute")
            .format("YYYY-MM-DD HH:mm:ss") &&
        dayjs(timeEnd).startOf("minute").format("YYYY-MM-DD HH:mm:ss") ===
          dayjs(calcIntervl.timeEnd)
            .startOf("minute")
            .format("YYYY-MM-DD HH:mm:ss")
      ) {
        return true;
      }
    });

    setIntervalSelected(autoIntervalSelect);
  }, [timeStart, timeEnd]);

  const handleChangeDateTimeStart = (value: dayjs.Dayjs | null) => {
    setIntervalSelected(null);
    onChange({
      timeStart: value ? value.toDate() : null,
      timeEnd: timeEnd,
    });
  };

  const handleChangeDateTimeEnd = (value: dayjs.Dayjs | null) => {
    setIntervalSelected(null);
    onChange({
      timeStart: timeStart,
      timeEnd: value ? value.toDate() : null,
    });
  };

  const calculateInterval = (option: Option) => {
    if (option.type === "ago")
      return {
        timeStart: dayjs()
          .subtract(option.value, option.unitEn as dayjs.ManipulateType)
          .toDate(),
        timeEnd: dayjs().toDate(),
        option: option,
      };
    if (option.type === "current")
      return {
        timeStart: dayjs()
          .startOf(option.unitEn as dayjs.ManipulateType)
          .toDate(),
        timeEnd: dayjs().toDate(),
        option: option,
      };

    if (option.type === "previous")
      return {
        timeStart: dayjs()
          .startOf(option.unitEn as dayjs.ManipulateType)
          .subtract(1, option.unitEn as dayjs.ManipulateType)
          .toDate(),
        timeEnd: dayjs()
          .endOf(option.unitEn as dayjs.ManipulateType)
          .subtract(1, option.unitEn as dayjs.ManipulateType)
          .toDate(),
        option: option,
      };
    return {
      timeStart: dayjs().subtract(1, "month").toDate(),
      timeEnd: dayjs().toDate(),
    };
  };

  const handleMenuItemClick = (option: Option) => {
    setIntervalSelected(option);
    onChange(calculateInterval(option));
  };

  const textOptions = (option: Option) => {
    if (option.type === "ago") {
      return ` ${option.value} ${option.unit} atrás`;
    }
    if (option.type === "current") {
      return `${option.unit} atual`;
    }
    if (option.type === "previous") {
      return `${option.unit} anterior`;
    }
  };
  return (
    <Popover
      id={"modal-data"}
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      sx={styles.paperPoover}
    >
      <>
        <Stack direction="row" spacing={1}>
          <Stack direction="column" spacing={2}>
            <Typography sx={styles.titleDataPicker}>
              Intervalos de tempo absoluto
            </Typography>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={"pt-br"}
            >
              <Stack direction="column" spacing={1}>
                <Stack
                  direction="column"
                  alignItems={"start"}
                  gap={0}
                  justifyContent={"space-between"}
                >
                  <DateTimePicker
                    label="Data Inicial"
                    value={dayjs(timeStart)}
                    onChange={(value) => handleChangeDateTimeStart(value)}
                    slotProps={styles.dataTime as any}
                    minutesStep={1}
                    maxDateTime={dayjs(timeEnd)}
                    disabled={disabled}
                  />
                </Stack>
                <Stack
                  direction="column"
                  alignItems={"start"}
                  gap={0}
                  justifyContent={"space-between"}
                >
                  <DateTimePicker
                    label="Data Final"
                    slotProps={styles.dataTime as any}
                    value={dayjs(timeEnd)}
                    onChange={(value) => handleChangeDateTimeEnd(value)}
                    minDateTime={dayjs(timeStart).add(1, "minute")}
                    // maxDateTime={dayjs().add(1, "minute")}
                    minutesStep={1}
                    disabled={disabled}
                  />
                </Stack>
              </Stack>
            </LocalizationProvider>
          </Stack>
          <Divider orientation="vertical" flexItem />
          <Stack direction="column" spacing={1}>
            <Typography sx={styles.titleDataPicker}>
              Intervalos de tempo relativo
            </Typography>
            <MenuList sx={styles.menuList}>
              {optionsTimesRange?.map((option) => (
                <MenuItem
                  selected={
                    intervalSeleted
                      ? intervalSeleted.value === option.value &&
                        intervalSeleted.unit === option.unit &&
                        intervalSeleted.unitEn === option.unitEn &&
                        intervalSeleted.type === option.type
                      : false
                  }
                  sx={styles.menuItem}
                  key={option.value + option.unit + option.type + option.unitEn}
                  onClick={() => handleMenuItemClick(option)}
                  disabled={disabled}
                >
                  {textOptions(option)}
                </MenuItem>
              ))}
            </MenuList>
          </Stack>
        </Stack>
      </>
    </Popover>
  );
};

export default ModalDateTime;
